import {
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  TextCapitalized,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useGridApiRef,
  GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
} from "@mui/x-data-grid-premium";
import "../../i18n/i18n";
import { BalanceLimitsDisplayed } from "./balanceLimitsApi";
import {
  companyTopUpsAsync,
  selectCompanyTopUps,
  selectCompanyTopUpsStatus,
} from "./balanceLimitsSlice";
import {
  companiesListAsync,
  selectCompaniesList,
  selectCompaniesListStatus,
} from "../companiesSlice";
import { formatDate, getDate } from "../../lib/materialUiFormats";
import { useTranslation } from "react-i18next";

export const BalanceLimitsTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const companyTopUps = useAppSelector(selectCompanyTopUps);
  const companyTopUpsStatus = useAppSelector(selectCompanyTopUpsStatus);
  const companyList = useAppSelector(selectCompaniesList);
  const companyListStatus = useAppSelector(selectCompaniesListStatus);

  const [listDisplayed, setListDisplayed] = useState<
    BalanceLimitsDisplayed[] | null
  >(null);

  const pageName = "balanceLimits";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      amountLimit: "avg",
      percentageLimit: "avg",
      executionDate: "size",
      companyName: "size",
      iban: "size",
      lastTopUp: "avg",
    },
  };

  const companyTopUpListWithId = useMemo(() => {
    const rowsWithId = companyTopUps
      .map((cd) => {
        const company = companyList.find((cf) => cf.iban === cd.iban);
        if (!company) return null;
        const newDict = {
          ...cd,
          id: cd.uuid,
          companyName: company.name,
          iban: company.iban,
        };
        return newDict;
      })
      .filter((cd) => cd !== null);
    return rowsWithId;
  }, [companyTopUps, companyList]);

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const searchBarProps = {
    values: companyTopUpListWithId,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  useEffect(() => {
    if (companyListStatus === "idle") {
      void dispatch(companiesListAsync());
    }
  }, [companyListStatus, dispatch]);

  useEffect(() => {
    if (companyTopUpsStatus === "idle") {
      void dispatch(companyTopUpsAsync());
    }
  }, [companyTopUpsStatus, dispatch]);

  const columnsDisplayed: {
    [index in keyof BalanceLimitsDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      companyName: {
        type: "string",
        width: 200,
      },
      iban: {
        type: "string",
        width: 200,
      },
      lastTopUp: {
        type: "number",
        width: 200,
        valueFormatter: (value: number) => <>{value} €</>,
      },
      percentageLimit: {
        type: "number",
        width: 200,
      },
      amountLimit: {
        type: "number",
        width: 360,
        valueFormatter: (value: number) => {
          return value ?? "-";
        },
      },
      alertAlreadySent: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          if (props.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
            return null;
          }
          const isAlertAlreadySent = props.row["alertAlreadySent"] as boolean;
          const alertText = isAlertAlreadySent
            ? t("balanceLimits.alertAlreadySent")
            : t("balanceLimits.alertNotSent");
          return (
            <HasAlertAlreadySentContainer>
              <TextCapitalized>{alertText}</TextCapitalized>
            </HasAlertAlreadySentContainer>
          );
        },
      },
      executionDate: {
        type: "date",
        width: 140,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
    };
  }, [t]);

  useEffect(() => {
    if (companyTopUpListWithId && companyTopUpsStatus === "success") {
      setListDisplayed(companyTopUpListWithId);
    }
  }, [companyTopUpListWithId, companyTopUpsStatus]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (params.tab !== "balanceLimits") {
    return null;
  }

  return (
    <StyledContainer>
      <Spacer y={2} />
      <MaterialUiTable<keyof BalanceLimitsDisplayed, BalanceLimitsDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HasAlertAlreadySentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;
